import React, {useRef, useEffect, useCallback} from 'react';
import {observer} from 'mobx-react';
import * as util from '../util';

declare const modalCloseById: (id: string) => void;

interface IModalSimpleElementProps {
    id: string;
    title: string;
    open?: boolean;
    maxWidth?: string;
    children: JSX.Element;
    abTestClass?: string;
    closeButtonText?: string;
    closeButtonClass?: string;
    closeButtonDisabled?: boolean;
    close?: () => void;
    saveAndClose?: () => void;
}

const ModalSimple: React.FC<IModalSimpleElementProps> = observer(props => {
    const modalRef = useRef<HTMLDivElement>(null);
    const observerRef = useRef<MutationObserver | null>(null);
    const triggeredFocusElementRef = useRef<HTMLElement | null>(null);
    const focusableElementsRef = useRef<HTMLElement[]>([]);
    const keyListenerRef = useRef<((e: KeyboardEvent) => void) | null>(null);

    const activateFocusTrap = useCallback(() => {
        if (!modalRef.current) return;

        focusableElementsRef.current = Array.from(
            modalRef.current.querySelectorAll<HTMLElement>(`
                a[href], area[href], input:not([disabled]),
                select:not([disabled]), textarea:not([disabled]),
                button:not([disabled]), iframe, object,
                embed, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]
            `),
        ).filter(el => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden'));

        if (focusableElementsRef.current.length === 0) return;

        focusableElementsRef.current[0].focus();

        if (keyListenerRef.current) {
            document.removeEventListener('keydown', keyListenerRef.current);
        }

        keyListenerRef.current = (e: KeyboardEvent) => {
            if (e.key !== 'Tab') return;

            const first = focusableElementsRef.current[0];
            const last = focusableElementsRef.current[focusableElementsRef.current.length - 1];
            const active = document.activeElement;

            if (e.shiftKey) {
                if (active === first) {
                    e.preventDefault();
                    last.focus();
                }
            } else {
                if (active === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };

        document.addEventListener('keydown', keyListenerRef.current);
    }, []);

    const show = useCallback(() => {
        util.modal(props.id);
    }, [props.id]);

    const hide = useCallback(
        (saveButton: boolean = false) => {
            setTimeout(() => {
                modalCloseById(props.id);

                if (props.close) {
                    props.close();
                }

                if (saveButton && props.saveAndClose) {
                    props.saveAndClose();
                }

                if (triggeredFocusElementRef.current) {
                    triggeredFocusElementRef.current.focus();
                }
            });
        },
        [props.id, props.close, props.saveAndClose],
    );

    const onOutsideClick = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            if (!e.target) return;

            if ((e.target as HTMLElement).classList.contains('modal')) {
                hide();
            }
        },
        [hide],
    );

    const onKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLButtonElement>, saveButton: boolean = false) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                hide(saveButton);
            }
        },
        [hide],
    );

    useEffect(() => {
        // Subscribe to modal state
        if (modalRef.current) {
            observerRef.current = new MutationObserver(mutations => {
                mutations.forEach(mutation => {
                    const target = mutation.target as HTMLElement;

                    triggeredFocusElementRef.current = document.activeElement as HTMLElement;

                    if (getComputedStyle(target).display !== 'none') {
                        target.focus();
                        activateFocusTrap();
                    }
                });
            });

            observerRef.current.observe(modalRef.current, {
                attributes: true,
                attributeFilter: ['style'],
            });
        }

        if (props.open) {
            show();
            activateFocusTrap();
        }

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }

            if (keyListenerRef.current) {
                document.removeEventListener('keydown', keyListenerRef.current);
                keyListenerRef.current = null;
            }
        };
    }, []);

    const modalDescriptionId = `${props.id}-description`;
    const hasDescription = React.Children.toArray(props.children).some(child => React.isValidElement(child) && typeof child.props.children === 'string');

    return (
        <div
            className={`modal ${props.abTestClass || ''}`}
            id={props.id}
            tabIndex={-1}
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={`${props.id}-title`}
            aria-describedby={hasDescription ? modalDescriptionId : undefined}
            onClick={e => onOutsideClick(e)}
        >
            <div
                className="modal-dialog"
                style={{
                    maxWidth: props.maxWidth ? `${props.maxWidth}` : '700px',
                    maxHeight: window.innerHeight * 0.9,
                }}
            >
                <div className="modal-content">
                    <div
                        className="modal-header"
                        role="heading"
                        aria-level={1}
                    >
                        <span id={`${props.id}-title`}>{props.title}</span>
                        <button
                            type="button"
                            className="btn-close"
                            aria-label="Close"
                            onClick={() => hide()}
                            onKeyDown={e => onKeyDown(e)}
                        ></button>
                    </div>
                    <div
                        className="modal-body"
                        id={hasDescription ? modalDescriptionId : undefined}
                    >
                        {props.children}
                    </div>
                    <div className="modal-footer">
                        <button
                            type="button"
                            className={`btn btn-primary ${props.closeButtonClass}`}
                            disabled={props.closeButtonDisabled}
                            onClick={() => hide(true)}
                            onKeyDown={e => onKeyDown(e, true)}
                        >
                            {props.closeButtonText || 'Close'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default ModalSimple;
